import type { CSSProperties } from "react";
import { LEGACY_DEFAULT_VIEWS } from "@/config/views.data";
import { isUnsafeObjectKey, slugify, stripDiacritics, toText } from "./text";
import type { LegacyViewDefinition, View, ViewValue } from "./types";

/** Column layouts a view may reference. */
const VIEW_COLUMN_LAYOUT_IDS = ["ageGroups", "tiltaksklasse", "initialFiltering"] as const;

/**
 * Built-in views whose identity (source column, type, normalizer) must never
 * change, because stored interventions are classified through them.
 */
const BUILT_IN_VIEW_IDENTITY: Record<string, [string, View["type"], View["normalizer"]]> = {
  A: ["Kunnskapsgrunnlag", "numericPrefix", "default"],
  B: ["InterventionLevel", "numericPrefix", "default"],
  C: ["Initial_filtering", "numericPrefix", "default"],
  D: ["Ressursbehov", "categorical", "resource"],
  E: ["Grunnmur", "categorical", "default"],
  F: ["EvidenceLevel", "numericPrefix", "default"],
  G: ["Tiltaksklasse", "numericPrefix", "default"],
  Z: ["EvidenceLevel", "numericPrefix", "default"],
};

const PROTECTED_COLUMNS =
  /^(id|title(_no|_en)?|description(_no|_en)?|arena|region|youngest|oldest|links|kildeliste|objectives?|data.*|location|status|item_number|source_item_number)$/i;
const NAMED_COLORS: Record<string, string> = { green: "#008000", yellow: "#ffff00" };
const DEFAULT_STRIPE_BACKGROUND = "#A6A6A6";

/** Converts the legacy CSS-string view format into the structured `View`. */
function fromLegacyView(view: LegacyViewDefinition, index: number): View {
  const values = Object.fromEntries(
    Object.entries(view.values).map(([code, value]): [string, ViewValue] => {
      const color = NAMED_COLORS[value.color] ?? value.color;
      const css = value.extraCSS ?? "";
      const textColor = /(?:^|;)color:#000;/.test(css)
        ? "#000000"
        : /(?:^|;)color:#fff;/.test(css)
          ? "#ffffff"
          : "auto";
      return [
        code,
        {
          label: { ...value.label },
          color,
          pattern: css.includes("repeating-linear-gradient") ? "striped" : "solid",
          stripeColor: css.match(/45deg,(#[0-9a-f]{6})/i)?.[1] ?? color,
          stripeBackground: DEFAULT_STRIPE_BACKGROUND,
          textColor,
        },
      ];
    }),
  );
  return {
    id: view.id,
    column: view.column,
    type: view.type,
    normalizer: view.normalize === "resource" ? "resource" : "default",
    label: { ...view.label },
    columnLayoutId: view.columnLayoutId,
    detailsViewIds: [...view.detailsViewIds],
    chipDisplay: view.chipDisplay,
    visible: true,
    sortOrder: index,
    order: view.order ? [...view.order] : Object.keys(view.values),
    values,
  };
}

/** Built-in views used until an administrator saves view settings. */
export const DEFAULT_VIEWS: View[] = LEGACY_DEFAULT_VIEWS.map(fromLegacyView);

export function sortViews(views: View[]): View[] {
  return [...views].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
}

/** Finds a view, falling back to the first visible view. */
export function getViewById(views: View[], id: string | null | undefined): View {
  const found = views.find((view) => view.id === id);
  const fallback = views.find((view) => view.visible) ?? views[0];
  const view = found ?? fallback;
  if (!view) throw new Error("No views configured");
  return view;
}

/** Normalizes Norwegian resource levels ("Høyt", "middels", "lav") to codes. */
function normalizeResource(value: unknown): string {
  const raw = toText(value);
  if (!raw) return "";
  // "ø" is its own letter (no NFD decomposition), so map it explicitly.
  const plain = stripDiacritics(raw.replace(/ø/gi, "o")).toLowerCase();
  if (/^hoyt?$/.test(plain)) return "hoy";
  if (plain === "middels") return "middels";
  if (/^lavt?$/.test(plain)) return "lavt";
  return "";
}

/** Derives a view's value code from a raw cell value ("" when unclassified). */
export function getViewCode(view: View, rawValue: unknown): string {
  const raw = toText(rawValue);
  if (view.type === "numericPrefix") return /^[0-9]/.test(raw) ? raw.charAt(0) : "";
  if (view.normalizer === "resource") {
    const resource = normalizeResource(raw);
    if (resource) return resource;
    const code = slugify(raw);
    return Object.hasOwn(view.values, code) ? code : "";
  }
  return slugify(raw);
}

/** Inline style for a classification value (background, stripes, text). */
export function getViewValueStyle(value: ViewValue | undefined): CSSProperties {
  if (!value) return {};
  const style: CSSProperties = { backgroundColor: value.color };
  if (value.pattern === "striped") {
    style.backgroundImage = `repeating-linear-gradient(45deg,${value.stripeColor} 0,${value.stripeColor} 5px,${value.stripeBackground} 5px,${value.stripeBackground} 10px)`;
  }
  if (value.textColor !== "auto") style.color = value.textColor;
  return style;
}

// ---------------------------------------------------------------------------
// Validation (mirrors the database check in supabase/schema.sql)
// ---------------------------------------------------------------------------

const isLabelValid = (label: unknown): boolean => {
  const value = label as Partial<Record<string, unknown>> | null;
  return (
    !!value &&
    typeof value === "object" &&
    typeof value.en === "string" &&
    Boolean(value.en.trim()) &&
    typeof value.no === "string"
  );
};

const isSafeKey = (value: unknown): value is string =>
  typeof value === "string" && !isUnsafeObjectKey(value);

/** Built-in views are referenced by code and can only be hidden; custom views can be deleted. */
export function isBuiltInView(id: string): boolean {
  return Object.hasOwn(BUILT_IN_VIEW_IDENTITY, id);
}

export function isViewSourceColumn(column: unknown): boolean {
  return (
    typeof column === "string" &&
    /^[\p{L}\p{N}_ ()-]{1,80}$/u.test(column) &&
    column === column.trim() &&
    !PROTECTED_COLUMNS.test(column) &&
    isSafeKey(column)
  );
}

export type ViewValidationError =
  "views" | "id" | "view" | "identity" | "values" | "details" | "visible";

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function validateValue(view: View, code: string, value: ViewValue | undefined): boolean {
  const codePattern = view.type === "numericPrefix" ? /^[0-9]$/ : /^[a-z0-9][a-z0-9-]{0,39}$/;
  return (
    isSafeKey(code) &&
    codePattern.test(code) &&
    !!value &&
    isLabelValid(value.label) &&
    HEX_COLOR.test(value.color) &&
    ["solid", "striped"].includes(value.pattern) &&
    HEX_COLOR.test(value.stripeColor) &&
    HEX_COLOR.test(value.stripeBackground) &&
    ["auto", "#000000", "#ffffff"].includes(value.textColor)
  );
}

/** Returns the first validation problem in a set of views, or `null`. */
export function getViewSettingsValidationError(views: unknown): ViewValidationError | null {
  if (!Array.isArray(views) || !views.length || views.length > 50) return "views";
  const list = views as View[];
  const ids = new Set<string>();
  for (const view of list) {
    if (
      !view ||
      !isSafeKey(view.id) ||
      !/^[A-Za-z][A-Za-z0-9_-]{0,31}$/.test(view.id) ||
      ids.has(view.id)
    ) {
      return "id";
    }
    ids.add(view.id);
    if (
      !isLabelValid(view.label) ||
      !isViewSourceColumn(view.column) ||
      !["numericPrefix", "categorical"].includes(view.type) ||
      !["default", "resource"].includes(view.normalizer) ||
      (view.normalizer === "resource" && view.type !== "categorical") ||
      !(VIEW_COLUMN_LAYOUT_IDS as readonly string[]).includes(view.columnLayoutId) ||
      !["hidden", "view", "objectives"].includes(view.chipDisplay) ||
      typeof view.visible !== "boolean" ||
      !Number.isInteger(view.sortOrder) ||
      view.sortOrder < -2147483648 ||
      view.sortOrder > 2147483647
    ) {
      return "view";
    }
    const identity = BUILT_IN_VIEW_IDENTITY[view.id];
    if (
      identity &&
      identity.some((value, index) => value !== [view.column, view.type, view.normalizer][index])
    ) {
      return "identity";
    }
    const codes =
      view.values && typeof view.values === "object" && !Array.isArray(view.values)
        ? Object.keys(view.values)
        : null;
    if (
      !codes ||
      !codes.length ||
      codes.length > 100 ||
      !Array.isArray(view.order) ||
      view.order.length !== codes.length ||
      new Set(view.order).size !== view.order.length ||
      view.order.some((code) => !Object.hasOwn(view.values, code)) ||
      codes.some((code) => !validateValue(view, code, view.values[code]))
    ) {
      return "values";
    }
    if (
      !Array.isArray(view.detailsViewIds) ||
      new Set(view.detailsViewIds).size !== view.detailsViewIds.length
    ) {
      return "details";
    }
  }
  if (Object.keys(BUILT_IN_VIEW_IDENTITY).some((id) => !ids.has(id))) return "identity";
  if (!list.some((view) => view.visible)) return "visible";
  if (list.some((view) => view.detailsViewIds.some((id) => !ids.has(id)))) return "details";
  return null;
}

/** Views whose rows are listed in the details panel for the selected view. */
export function getDetailViews(views: View[], selected: View): View[] {
  return selected.detailsViewIds
    .map((id) => views.find((view) => view.id === id))
    .filter((view): view is View => !!view && view.visible);
}
